import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, unref as _unref, createBlock as _createBlock, normalizeStyle as _normalizeStyle, Teleport as _Teleport } from "vue"

const _hoisted_1 = ["onClick"]
const _hoisted_2 = {
  type: "button",
  class: "shrink-0 rounded-full border px-3 py-1.5 text-sm font-medium"
}
const _hoisted_3 = ["onClick"]
const _hoisted_4 = { class: "truncate" }

import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { onClickOutside } from '@vueuse/core';
import { Check } from 'lucide-vue-next';
import type { Farm } from '@/types';


export default /*@__PURE__*/_defineComponent({
  __name: 'FarmButtonPicker',
  props: {
    farms: {},
    modelValue: {}
  },
  emits: ["update:modelValue"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

function isSelected(farmId: number): boolean {
    return props.modelValue === farmId;
}

// Mutually exclusive: picking a farm always selects it. Re-clicking the already-selected farm is a
// no-op rather than clearing the selection - the rest of the page assumes a farm is always chosen
// once one has been.
function selectFarm(farmId: number): void {
    if (props.modelValue !== farmId) {
        emit('update:modelValue', farmId);
    }
    overflowOpen.value = false;
}

const visibleFarmCount = ref(Infinity);
const visibleFarms = computed(() => props.farms.slice(0, visibleFarmCount.value));
const hiddenFarms = computed(() => props.farms.slice(visibleFarmCount.value));
const hiddenSelected = computed(() => hiddenFarms.value.some((farm) => isSelected(farm.farm_id)));

const farmRowRef = ref<HTMLElement | null>(null);
const measureRowRef = ref<HTMLElement | null>(null);
const overflowButtonRef = ref<HTMLElement | null>(null);
const overflowPopoverRef = ref<HTMLElement | null>(null);
const overflowOpen = ref(false);
const overflowPopoverStyle = ref<{ top: string; left: string }>({ top: '0px', left: '0px' });

function toggleOverflowPopover(): void {
    if (!overflowOpen.value && overflowButtonRef.value) {
        const rect = overflowButtonRef.value.getBoundingClientRect();
        overflowPopoverStyle.value = { top: `${rect.bottom + 4}px`, left: `${rect.right - 224}px` };
    }
    overflowOpen.value = !overflowOpen.value;
}

onClickOutside(
    overflowPopoverRef,
    () => {
        overflowOpen.value = false;
    },
    { ignore: [overflowButtonRef] }
);

// Sums the off-screen measuring row's real chip widths against the visible row's available width,
// reserving room for the "+N" chip unless every farm already fits without it.
function remeasureFarmRow(): void {
    const container = farmRowRef.value;
    const measureEl = measureRowRef.value;
    if (!container || !measureEl || !props.farms.length) return;

    const children = Array.from(measureEl.children) as HTMLElement[];
    const overflowChipWidth = children[children.length - 1].offsetWidth;
    const chipWidths = children.slice(0, -1).map((chip) => chip.offsetWidth);
    const gap = 8;
    const available = container.clientWidth;

    let used = 0;
    let count = 0;
    for (let i = 0; i < chipWidths.length; i += 1) {
        const isLastChip = i === chipWidths.length - 1;
        const next = used + chipWidths[i] + (i > 0 ? gap : 0);
        const reserveForOverflowButton = isLastChip ? 0 : overflowChipWidth + gap;
        if (next + reserveForOverflowButton > available) break;
        used = next;
        count += 1;
    }
    visibleFarmCount.value = count;
}

let farmRowObserver: ResizeObserver | null = null;

onMounted(() => {
    if (!farmRowRef.value) return;
    farmRowObserver = new ResizeObserver(() => remeasureFarmRow());
    farmRowObserver.observe(farmRowRef.value);
});

onUnmounted(() => {
    farmRowObserver?.disconnect();
    farmRowObserver = null;
});

watch(() => props.farms, () => nextTick(remeasureFarmRow));

watch(
    () => hiddenFarms.value.length,
    (count) => {
        if (count === 0) overflowOpen.value = false;
    }
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", {
      ref_key: "farmRowRef",
      ref: farmRowRef,
      class: "relative flex min-w-0 flex-1 items-center gap-2 overflow-hidden"
    }, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleFarms.value, (farm) => {
        return (_openBlock(), _createElementBlock("button", {
          key: farm.farm_id,
          type: "button",
          class: _normalizeClass(["shrink-0 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors", isSelected(farm.farm_id) ? 'border-primary-700 bg-primary-700 text-white' : 'border-gray-300 bg-white text-gray-500 hover:bg-gray-100']),
          onClick: ($event: any) => (selectFarm(farm.farm_id))
        }, _toDisplayString(farm.name), 11, _hoisted_1))
      }), 128)),
      (hiddenFarms.value.length)
        ? (_openBlock(), _createElementBlock("button", {
            key: 0,
            ref_key: "overflowButtonRef",
            ref: overflowButtonRef,
            type: "button",
            class: _normalizeClass(["shrink-0 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors", hiddenSelected.value ? 'border-primary-700 bg-primary-700 text-white' : 'border-gray-300 bg-white text-gray-500 hover:bg-gray-100']),
            onClick: toggleOverflowPopover
          }, " +" + _toDisplayString(hiddenFarms.value.length), 3))
        : _createCommentVNode("", true),
      _createElementVNode("div", {
        ref_key: "measureRowRef",
        ref: measureRowRef,
        class: "pointer-events-none absolute left-0 top-0 flex items-center gap-2",
        style: {"visibility":"hidden"},
        "aria-hidden": "true",
        inert: ""
      }, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(props.farms, (farm) => {
          return (_openBlock(), _createElementBlock("button", {
            key: farm.farm_id,
            type: "button",
            class: "shrink-0 rounded-full border px-3 py-1.5 text-sm font-medium"
          }, _toDisplayString(farm.name), 1))
        }), 128)),
        _createElementVNode("button", _hoisted_2, "+" + _toDisplayString(props.farms.length), 1)
      ], 512)
    ], 512),
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      (overflowOpen.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            ref_key: "overflowPopoverRef",
            ref: overflowPopoverRef,
            class: "fixed z-[1030] max-h-64 w-56 overflow-y-auto rounded-lg border border-gray-200 bg-white p-1.5 shadow-lg",
            style: _normalizeStyle(overflowPopoverStyle.value)
          }, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(hiddenFarms.value, (farm) => {
              return (_openBlock(), _createElementBlock("button", {
                key: farm.farm_id,
                type: "button",
                class: "flex w-full items-center justify-between gap-2 rounded-md px-2.5 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-100",
                onClick: ($event: any) => (selectFarm(farm.farm_id))
              }, [
                _createElementVNode("span", _hoisted_4, _toDisplayString(farm.name), 1),
                (isSelected(farm.farm_id))
                  ? (_openBlock(), _createBlock(_unref(Check), {
                      key: 0,
                      class: "h-4 w-4 shrink-0 text-primary-700",
                      "aria-hidden": "true"
                    }))
                  : _createCommentVNode("", true)
              ], 8, _hoisted_3))
            }), 128))
          ], 4))
        : _createCommentVNode("", true)
    ]))
  ], 64))
}
}

})